import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import { connect, useSelector } from 'react-redux';
import Modal from '@mui/material/Modal';
import { useDispatch } from 'react-redux';
import { compose } from '@reduxjs/toolkit';
import Stepper from '@mui/material/Stepper';
import { useNavigate } from 'react-router-dom';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import Button from 'core/buttons/Button';
import Spacer from 'core/spacer/Spacer';
import CircularProgress from 'core/progress/CircularProgress';

import { buildRoute } from 'routes';
import ucwords from 'helpers/ucwords';
import {
  retryToCreate,
  setShowCreateProcessingModal,
  retryToUpdate,
  setShowEditProcessingModal,
} from 'app/features/licensing-partners/licensingPartnerSlice';

const style = {
  position: 'absolute',
  top: '64px',
  right: '0',
  width: 384,
  height: 'calc(100vh - 64px)',
  bgcolor: 'background.paper',
  border: 0,
  boxShadow: 24,
  p: 4,
};

function getStep1Label(isAddMode) {
  return isAddMode ? 'Creating New Licensing Partner' : 'Updating New Licensing Partner';
}

function getStep2Label(isAddMode, isProcessed, isFailed) {
  if (isAddMode) {
    return isProcessed
      ? 'Licensing Partner created successfully'
      : isFailed
      ? 'Failed to create Licening Partner'
      : '';
  }

  return isProcessed
    ? 'Licensing Partner created successfully'
    : 'Failed to create Licensing Partner';
}

function LicensingPartnerProgressIndicatorModal(props) {
  const params = useParams();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { isAddMode, open, isProcessing, isProcessed, isFailed, licensingPartner, errorMessage } =
    props;

  const steps = useMemo(() => {
    return [
      {
        label: getStep1Label(isAddMode),
        showProgress: true,
        color: 'primary',
      },
      {
        label: getStep2Label(isAddMode, isProcessed, isFailed),
        showProgress: false,
        color: isProcessed ? 'primary' : 'error',
      },
    ];
  }, [isAddMode, isProcessed, isFailed]);

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const hideProgressModal = () => {
    dispatch(isAddMode ? setShowCreateProcessingModal(false) : setShowEditProcessingModal(false));
  };

  useEffect(() => {
    if (isProcessing) handleNext();
  }, [isProcessing]);

  useEffect(() => {
    if (isProcessed) handleNext();
  }, [isProcessed]);

  return (
    <div>
      <Modal open={open} onClose={() => {}}>
        <Box sx={style}>
          <Button
            onClick={() => hideProgressModal()}
            variant="text"
            sx={{ minWidth: 0, paddingInline: 0, mb: 2 }}
          >
            <ArrowBackIcon />
          </Button>
          <Box sx={{ maxWidth: 400 }}>
            {/* Step process */}
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel color={step.color}>
                    <Box display="flex" gap={2} alignItems="center">
                      <Typography color={step.color}>{step.label}</Typography>
                      {isProcessing && step.showProgress && (
                        <CircularProgress size={30} thickness={6} />
                      )}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Spacer />
            {/* If Form Process Failed */}
            {isFailed && (
              <Box>
                <Typography variant="body2" color="error">
                  {ucwords(errorMessage)}
                </Typography>
                <Spacer />
                <Button
                  color="primary"
                  onClick={() => dispatch(isAddMode ? retryToCreate() : retryToUpdate())}
                  fullWidth
                >
                  Retry
                </Button>
                <Spacer />
                <Button
                  color="error"
                  onClick={() => hideProgressModal()}
                  variant="outlined"
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            )}
            {licensingPartner && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Box>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => (
                      hideProgressModal(),
                      navigate(
                        buildRoute(
                          'licensing-partners.show',
                          licensingPartner.id ?? params.licensingPartnerId,
                        ),
                      )
                    )}
                    fullWidth
                  >
                    View Details
                  </Button>
                </Box>
                <Box>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => (
                      hideProgressModal(), navigate(buildRoute('licensing-partners.index'))
                    )}
                    fullWidth
                  >
                    Licensing parter List
                  </Button>
                </Box>
                <Box>
                  <Button
                    type="button"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      hideProgressModal();
                      navigate(buildRoute('licensing-partners.create'));
                    }}
                    fullWidth
                  >
                    Add New Licensing Partner
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

export default compose(connect(mapStateToProps))(LicensingPartnerProgressIndicatorModal);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    open: ownProps.isAddMode
      ? state.licensingPartners.create.showProcessingModal
      : state.licensingPartners.update.showProcessingModal,
    isProcessing: ownProps.isAddMode
      ? state.licensingPartners.create.isCreating
      : state.licensingPartners.update.isUpdating,
    isProcessed: ownProps.isAddMode
      ? state.licensingPartners.create.isCreated
      : state.licensingPartners.update.isUpdated,
    isFailed: ownProps.isAddMode
      ? state.licensingPartners.create.isFailed
      : state.licensingPartners.update.isFailed,
    licensingPartners: ownProps.isAddMode
      ? state.licensingPartners.create.data
      : state.licensingPartners.update.data,
    errorMessage: ownProps.isAddMode
      ? state.licensingPartners.create.errorMessage
      : state.licensingPartners.update.errorMessage,
  };
}

LicensingPartnerProgressIndicatorModal.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  licensingPartner: PropTypes.object,
  open: PropTypes.bool.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  isProcessed: PropTypes.bool.isRequired,
  isFailed: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
};
